/* eslint-disable prettier/prettier */

import * as React from "react";
import {
  Grid,
  Link,
  Stack,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  Switch,
  Tooltip,
  Box,
  SvgIcon,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CreateIcon from "@mui/icons-material/Create";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import { useParams, useHistory } from "react-router-dom";

import { AuthenticationContext } from "../../../Context";
import { PrivilegesContext } from "../../../PrivilegesContext";
import { getHeaders } from "../../../utils/HeaderHelper";
import { checkPrivileges } from "../../../utils/PrivilegesHelper";
import { useFlash } from "../../../FlashContext";
import { Modal as DeleteModal } from "../../../modals/Modal";
import InterventionHistoryModal from "./InterventionHistoryModal";

const NOTES_PREVIEW_LENGTH = 120;

// Signed badge: background #EDF7EE, text #07BC2A, Quicksand 8px font-weight 600
const signedBadgeSx = {
  display: "flex",
  width: 100,
  padding: "0 8px",
  justifyContent: "center",
  alignItems: "center",
  flexShrink: 0,
  borderRadius: "10px",
  background: "#EDF7EE",
  color: "#07BC2A",
  fontFamily: "QuicksandMedium",
  fontSize: "10px",
  fontStyle: "normal",
  fontWeight: 100,
  lineHeight: "20px",
  letterSpacing: "0.4px",
};

// Pended badge (non-signed interventions) — #FFF0BE background, #B08800 text
const pendedBadgeSx = {
  display: "flex",
  width: 100,
  padding: "0 8px",
  justifyContent: "center",
  alignItems: "center",
  flexShrink: 0,
  borderRadius: "10px",
  background: "#FFF0BE",
  color: "#B08800",
  fontFamily: "QuicksandMedium",
  fontSize: "8px",
  fontStyle: "normal",
  fontWeight: 100,
  lineHeight: "20px",
  letterSpacing: "0.4px",
};

// Archived badge: red - distinct from active statuses
const archivedBadgeSx = {
  display: "flex",
  width: 100,
  padding: "0 8px",
  justifyContent: "center",
  alignItems: "center",
  flexShrink: 0,
  borderRadius: "10px",
  background: "#FFD9D9",
  color: "#F00",
  fontFamily: "QuicksandMedium",
  fontSize: "10px",
  fontStyle: "normal",
  fontWeight: 600,
  lineHeight: "20px",
  letterSpacing: "0.4px",
};

const overviewDateSx = {
  color: "#898989",
  fontFamily: "QuicksandMedium",
  fontSize: "12px",
  fontStyle: "normal",
  fontWeight: 500,
  lineHeight: "18px",
};

const overviewTypeSx = {
  color: "#1E1E1E",
  fontFamily: "QuicksandBold",
  fontSize: "16px",
  fontStyle: "normal",
  fontWeight: 700,
  lineHeight: "18px",
};

const overviewActivitySx = {
  color: "#1E1E1E",
  fontFamily: "QuicksandMedium",
  fontSize: "12px",
  fontStyle: "normal",
  fontWeight: 500,
  lineHeight: "16px",
};

const tableHeaderCellSx = {
  fontFamily: "QuicksandBold",
  color: "#000",
  fontSize: 12,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const centeredTableHeaderCellSx = {
  ...tableHeaderCellSx,
  textAlign: "center",
};

const diagnosisCodeSx = {
  color: "#1E1E1E",
  fontFamily: "QuicksandBold",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 700,
  lineHeight: "16px",
};

const diagnosisDescriptionSx = {
  color: "#1E1E1E",
  fontFamily: "QuicksandMedium",
  fontSize: "14px",
  fontStyle: "normal",
  fontWeight: 500,
  lineHeight: "16px",
};

const iconButtonSx = { color: "black", cursor: "pointer" };

const inlineLinkSx = {
  color: "#0B34D8",
  fontFamily: "QuicksandMedium",
  fontSize: "13px",
  fontStyle: "normal",
  fontWeight: 500,
  lineHeight: "18px",
  textDecorationLine: "underline",
  textDecorationStyle: "solid",
  textDecorationSkipInk: "auto",
  textDecorationThickness: "auto",
  textUnderlineOffset: "auto",
  textUnderlinePosition: "from-font",
  display: "inline-flex",
  alignItems: "center",
  gap: "4px",
};

const ArchiveActionIcon = () => (
  <SvgIcon sx={{ width: 24, height: 24 }}>
    <path
      d="M20.54 5.23L19.15 3.55C18.88 3.21 18.47 3 18 3H6C5.53 3 5.12 3.21 4.84 3.55L3.46 5.23C3.17 5.57 3 6.02 3 6.5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V6.5C21 6.02 20.83 5.57 20.54 5.23ZM12 17.5L6.5 12H10V10H14V12H17.5L12 17.5ZM5.12 5L5.93 4H17.93L18.87 5H5.12Z"
      fill="black"
    />
  </SvgIcon>
);

const EncounterLinkIcon = () => (
  <SvgIcon
    viewBox="0 0 12 12"
    sx={{
      width: 12,
      height: 12,
      display: "block",
      flexShrink: 0,
      position: "relative",
      top: "1px",
    }}
  >
    <path
      d="M9.66667 6.61111V10.2778C9.66667 10.6019 9.5379 10.9128 9.30869 11.142C9.07947 11.3712 8.7686 11.5 8.44444 11.5H1.72222C1.39807 11.5 1.08719 11.3712 0.857981 11.142C0.628769 10.9128 0.5 10.6019 0.5 10.2778V3.55556C0.5 3.2314 0.628769 2.92052 0.857981 2.69131C1.08719 2.4621 1.39807 2.33333 1.72222 2.33333H5.38889M7.83333 0.5H11.5M11.5 0.5V4.16667M11.5 0.5L4.77778 7.22222"
      stroke="#0B34D8"
      strokeWidth="1"
      strokeLinecap="round"
      fill="none"
    />
  </SvgIcon>
);

interface Props {}

const InterventionsPanel: React.FC<Props> = () => {
  const authenticationSetting = React.useContext(AuthenticationContext);
  const userPrivileges = React.useContext(PrivilegesContext);
  const canArchiveIntervention = !!checkPrivileges(
    userPrivileges,
    "Archive Intervention Note",
  );
  const { showToast } = useFlash();
  const history = useHistory();
  const { id: patientId } = useParams<{ id: string }>();

  const [interventions, setInterventions] = React.useState<any[]>([]);
  const [showArchived, setShowArchived] = React.useState(false);
  const [expandedNotesId, setExpandedNotesId] = React.useState<number | null>(null);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [confirmRow, setConfirmRow] = React.useState<{ id: number; status: string } | null>(null);
  const [confirmAction, setConfirmAction] = React.useState<"delete" | "archive">("delete");
  const [actionInProgress, setActionInProgress] = React.useState(false);
  const [historyInterventionId, setHistoryInterventionId] = React.useState<number | null>(null);

  const getInterventionData = React.useCallback(() => {
    if (!patientId) return;
    const url = showArchived
      ? `/interventions/index/${patientId}?include_archived=true`
      : `/interventions/index/${patientId}`;
    fetch(url, {
      method: "GET",
      headers: getHeaders(authenticationSetting.csrfToken),
    })
      .then((res) => res.json())
      .then((result) => {
        if (result.success === false) {
          showToast({
            title: "Interventions Load Error",
            text: result.error || "Failed to load interventions.",
            type: "error",
          });
        } else {
          setInterventions(result?.resource ?? []);
        }
      })
      .catch((error) => {
        showToast({
          title: "Interventions Load Error",
          text: error.message || "Failed to load interventions.",
          type: "error",
        });
      });
  }, [patientId, showArchived, authenticationSetting.csrfToken, showToast]);

  React.useEffect(() => {
    getInterventionData();
  }, [getInterventionData]);

  const renderStatus = (row: any) => {
    if (row.is_archived) {
      return (
        <Box sx={archivedBadgeSx}>
          Archived
        </Box>
      );
    }
    const isSigned = row.status === "signed";
    const label = isSigned ? "Signed" : "Pended";
    return (
      <Box sx={isSigned ? signedBadgeSx : pendedBadgeSx}>
        {label}
      </Box>
    );
  };

  const renderReviewRequest = (isReadyForReview: boolean) => {

    return (
      <Box
        sx={{
          width: 19,
          height: 19,
          flexShrink: 0,
          borderRadius: "3px",
          border: "2px solid #FF890A",
          backgroundColor: isReadyForReview ? "#FF890A" : "transparent",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxSizing: "border-box",
        }}
      >
        {isReadyForReview ? <CheckIcon sx={{ color: "#FFFFFF", fontSize: 14 }} /> : null}
      </Box>
    );
  };

  const renderNotesPreview = (intervention: any) => {
    const notes = intervention.notes || intervention.notes_preview || "";
    const isExpanded = expandedNotesId === intervention.id;
    const shouldTruncate = notes.length > NOTES_PREVIEW_LENGTH && !isExpanded;
    const displayText = shouldTruncate ? notes.slice(0, NOTES_PREVIEW_LENGTH) + "..." : notes;

    return (
      <span>
        {displayText}
        {notes.length > NOTES_PREVIEW_LENGTH && (
          <Link
            component="button"
            variant="body2"
            onClick={() => setExpandedNotesId(isExpanded ? null : intervention.id)}
            sx={{ ...inlineLinkSx, ml: 0.5 }}
          >
            {isExpanded ? " Show less" : " Read more"}
          </Link>
        )}
      </span>
    );
  };

  const renderMedications = (row: any) => {
    const text =
      row.related_medication != null
        ? String(row.related_medication).trim()
        : "";
    if (!text) {
      return "—";
    }
    return (
      <Box
        component="span"
        sx={{
          ...diagnosisDescriptionSx,
          wordBreak: "break-word",
        }}
      >
        {text}
      </Box>
    );
  };

  const renderRelatedConditionDx = (row: any) => {
    if (!row.related_condition_code && !row.related_condition_description) {
      return "—";
    }

    if (row.related_condition_code && row.related_condition_description) {
      return (
        <Box component="span">
          <Box component="span" sx={diagnosisCodeSx}>
            {row.related_condition_code}
          </Box>
          <Box component="span" sx={diagnosisDescriptionSx}>
            {" - "}
            {row.related_condition_description}
          </Box>
        </Box>
      );
    }

    return row.related_condition_code ? (
      <Box component="span" sx={diagnosisCodeSx}>
        {row.related_condition_code}
      </Box>
    ) : (
      <Box component="span" sx={diagnosisDescriptionSx}>
        {row.related_condition_description}
      </Box>
    );
  };

  const renderViewEditAction = (row: any) =>
    row.status === "pended" ? (
      <a
        title="Edit"
        href={`/patient_reports/${patientId}/interventions/new?intervention_id=${row.id}`}
      >
        <CreateIcon sx={iconButtonSx} />
      </a>
    ) : (
      <a
        title="View"
        href={`/patient_reports/${patientId}/interventions/new?intervention_id=${row.id}&view_only=1`}
      >
        <VisibilityIcon sx={iconButtonSx} />
      </a>
    );

  const renderRelatedEncounter = (row: any) => {
    if (!row.encounter_billing_id || !row.encounter_billing_uuid) {
      return "—";
    }

    return (
      <Link
        href={`/patient_reports/${patientId}/patient_encounters/new?encounter_id=${row.encounter_billing_uuid}&view_only=1`}
        target="_blank"
        rel="noopener noreferrer"
        sx={inlineLinkSx}
      >
        <span>View Detail</span>
        <EncounterLinkIcon />
      </Link>
    );
  };

  const openConfirm = (row: any, action: "delete" | "archive") => {
    setConfirmRow({ id: row.id, status: row.status });
    setConfirmAction(action);
    setConfirmOpen(true);
  };

  const closeConfirm = () => {
    if (!actionInProgress) {
      setConfirmOpen(false);
      setConfirmRow(null);
    }
  };

  const handleConfirmRemove = () => {
    if (!confirmRow || !patientId) return;
    setActionInProgress(true);
    const isDelete = confirmAction === "delete";
    const url = isDelete ? `/interventions/${confirmRow.id}` : `/interventions/${confirmRow.id}/archive`;
    const method = isDelete ? "DELETE" : "PATCH";
    fetch(url, {
      method,
      headers: getHeaders(authenticationSetting.csrfToken),
    })
      .then((res) => res.json())
      .then((result) => {
        if (result.success) {
          showToast({
            title: isDelete ? "Intervention deleted" : "Intervention archived",
            text: result.message || (isDelete ? "The intervention has been permanently removed." : "The intervention has been archived."),
            type: "success",
          });
          getInterventionData();
          setConfirmOpen(false);
          setConfirmRow(null);
        } else {
          showToast({
            title: isDelete ? "Delete failed" : "Archive failed",
            text: result.error || result.message || "Action could not be completed.",
            type: "error",
          });
        }
      })
      .catch((err) => {
        showToast({
          title: isDelete ? "Delete failed" : "Archive failed",
          text: err.message || "Action could not be completed.",
          type: "error",
        });
      })
      .finally(() => setActionInProgress(false));
  };

  const renderArchiveDeleteAction = (row: any) => {
    if (row.is_archived) {
      return null;
    }
    if (row.status === "pended") {
      return (
        <Tooltip title="Delete" arrow>
          <Link component="button" onClick={() => openConfirm(row, "delete")} sx={iconButtonSx}>
            <DeleteIcon sx={{ fontSize: 24 }} />
          </Link>
        </Tooltip>
      );
    }
    if (!canArchiveIntervention) {
      return null;
    }
    return (
      <Tooltip title="Archive" arrow>
        <Link component="button" onClick={() => openConfirm(row, "archive")} sx={iconButtonSx}>
          <ArchiveActionIcon />
        </Link>
      </Tooltip>
    );
  };

  const renderRowActions = (row: any) => (
    <Stack direction="row" alignItems="center" justifyContent="center" sx={{ flexWrap: "nowrap", gap: "12px" }}>
      <Tooltip title="History" arrow>
        <Link component="button" onClick={() => setHistoryInterventionId(row.id)} sx={iconButtonSx}>
          <AccessTimeOutlinedIcon sx={{ ...iconButtonSx, fontSize: 24 }} />
        </Link>
      </Tooltip>
      {renderViewEditAction(row)}
      {renderArchiveDeleteAction(row)}
    </Stack>
  );

  const confirmTitle = confirmAction === "delete" ? "Delete Intervention?" : "Archive Intervention?";
  const confirmMessage =
    confirmAction === "delete"
      ? "Are you sure you want to delete this intervention? This cannot be undone."
      : "Are you sure you want to archive this intervention? It will be removed from the active list.";
  const confirmMessageLines = confirmMessage.split("\n").map((line, index) => (
    <React.Fragment key={index}>
      {line}
      <br />
    </React.Fragment>
  ));

  return (
    <>
      <DeleteModal
        successModalOpen={confirmOpen}
        setSuccessModalOpen={setConfirmOpen}
        successHeader={confirmTitle}
        successContent={confirmMessageLines}
        successCallback={handleConfirmRemove}
        closeCallback={closeConfirm}
        confirmButtonText="Continue"
        isLoading={actionInProgress}
        width="500px"
        buttonWidth="154px"
        padding="24px 120px"
      />
      <InterventionHistoryModal
        open={historyInterventionId !== null}
        interventionId={historyInterventionId}
        onClose={() => setHistoryInterventionId(null)}
        csrfToken={authenticationSetting.csrfToken}
      />
      <Grid container className="panel-container">
      <Grid item xs={12}>
        <Grid container className="panel-show-container">
          <Grid container className="panel-information-container">
            <Grid container direction="row" className="admin-header">
              <Grid item xs={12} className="box-header">
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  paddingX={3}
                  paddingY={1}
                  alignItems="center"
                  display="flex"
                >
                  <Grid item>
                    <h3>Interventions</h3>
                  </Grid>
                  <Stack direction="row" spacing={3} alignItems="center">
                    <span
                      style={{
                        fontFamily: "QuicksandMedium",
                        fontSize: "14px",
                        color: "#666",
                        marginRight: "16px",
                      }}
                    >
                      <Switch
                        checked={showArchived}
                        onChange={() => setShowArchived(!showArchived)}
                        color="primary"
                      />
                      Show Archived Interventions
                    </span>
                    <Link
                      className="action-link add-encounter"
                      component="button"
                      onClick={() => history.push(`/patient_reports/${patientId}/interventions/new`)}
                    >
                      <AddIcon className="plus-icon" />
                      <span className="app-user-text" style={{ marginLeft: "8px" }}>
                        New Intervention
                      </span>
                    </Link>
                  </Stack>
                </Stack>
              </Grid>
            </Grid>
            <Grid container className="grey-container" style={{ marginTop: "-11px" }}>
              <Grid container className="tabs-container">
                <Grid
                  item
                  xs={12}
                  className="panel-body tab-body-container"
                  padding={0}
                  mt={1}
                  sx={{ border: "0px !important" }}
                >
                  <TableContainer>
                    <Table sx={{ tableLayout: "fixed", width: "100%" }}>
                      <TableHead className="table-header-box">
                        <TableRow>
                          <TableCell sx={tableHeaderCellSx} width="16%">
                            Overview
                          </TableCell>
                          <TableCell sx={tableHeaderCellSx} width="15%">
                            Related Condition/Dx
                          </TableCell>
                          <TableCell sx={tableHeaderCellSx} width="14%">
                            Medications
                          </TableCell>
                          <TableCell sx={tableHeaderCellSx} width="36%" title="Notes">
                            Notes
                          </TableCell>
                          <TableCell sx={tableHeaderCellSx} width="13%">
                            Linked Encounter
                          </TableCell>
                          <TableCell align="center" sx={centeredTableHeaderCellSx} width="7%">
                            Status
                          </TableCell>
                          <TableCell align="center" sx={centeredTableHeaderCellSx} width="10%">
                            Review Request
                          </TableCell>
                          <TableCell align="center" sx={centeredTableHeaderCellSx} width="12%" title="Actions">
                            Actions
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {interventions.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                              No interventions found.
                            </TableCell>
                          </TableRow>
                        ) : (
                          interventions.map((row) => (
                            <TableRow
                              key={row.id}
                              sx={{ "& td, & th": { borderBottom: "none" } }}
                            >
                              <TableCell className="date-data">
                                <Stack spacing={0.5}>
                                  <Box component="span" sx={overviewDateSx}>
                                    {row.occurred_on_date}
                                  </Box>
                                  <Box component="span" sx={overviewTypeSx}>
                                    {row.intervention_type || "—"}
                                  </Box>
                                  <Box component="span" sx={overviewActivitySx}>
                                    {row.activity || "—"}
                                  </Box>
                                </Stack>
                              </TableCell>
                              <TableCell>
                                {renderRelatedConditionDx(row)}
                              </TableCell>
                              <TableCell sx={{ maxWidth: 260 }}>
                                {renderMedications(row)}
                              </TableCell>
                              <TableCell sx={{ minWidth: 0, verticalAlign: "top" }}>
                                {row.notes ? renderNotesPreview(row) : "—"}
                              </TableCell>
                              <TableCell>
                                {renderRelatedEncounter(row)}
                              </TableCell>
                              <TableCell align="center">
                                <Box sx={{ display: "flex", justifyContent: "center" }}>
                                  {renderStatus(row)}
                                </Box>
                              </TableCell>
                              <TableCell align="center" sx={{ px: 0.5, width: "5%" }}>
                                <Box sx={{ display: "flex", justifyContent: "center" }}>
                                  {renderReviewRequest(!!row.is_ready_for_review)}
                                </Box>
                              </TableCell>
                              <TableCell align="center" sx={{ px: 0.5, width: "12%", whiteSpace: "nowrap" }}>
                                {renderRowActions(row)}
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
    </>
  );
};

export default InterventionsPanel;
